// Sample course data for testing the course interface
export const sampleCourse = {
  id: 'react-fundamentals-2024',
  title: 'React Fundamentals: Build Modern Web Applications',
  description: 'Master React.js from the ground up. Learn components, hooks, state management, and build real-world applications with modern React patterns and best practices.',
  shortDescription: 'Learn React.js fundamentals and build modern web applications with components, hooks, and state management.',
  category: 'Programming',
  industryCategory: 'Technology',
  difficulty: 'beginner' as const,
  duration: 40, // hours
  estimatedWeeks: 8,
  price: 0,
  instructorId: 'instructor-1',
  instructorName: 'Sarah Johnson',
  instructorBio: 'Senior Frontend Developer at Google with 8+ years of React experience. Passionate about teaching modern web development.',
  thumbnailUrl: '/images/courses/react.jpg',
  videoPreviewUrl: 'https://example.com/react-preview.mp4',
  status: 'published' as const,
  tags: ['React', 'JavaScript', 'Frontend', 'Web Development', 'Components', 'Hooks'],
  skills: ['React.js', 'JSX', 'Hooks', 'State Management', 'Component Design', 'Modern JavaScript'],
  prerequisites: ['Basic JavaScript', 'HTML/CSS'],
  learningObjectives: [
    'Build interactive React applications from scratch',
    'Master React components and JSX syntax',
    'Understand and use React Hooks effectively',
    'Manage component state and props',
    'Handle events and form submissions',
    'Create reusable UI components',
    'Deploy React applications to production'
  ],
  
  modules: [
    {
      id: 'module-1',
      title: 'Introduction to React',
      description: 'Get started with React basics, understanding what React is and setting up your development environment.',
      order: 1,
      estimatedHours: 4,
      estimatedMinutes: 240,
      content: [
        {
          id: 'content-1-1',
          type: 'video' as const,
          title: 'What is React?',
          description: 'Introduction to React and its core concepts',
          duration: 15,
          order: 1,
          videoUrl: 'https://example.com/react-intro.mp4',
          isRequired: true
        },
        {
          id: 'content-1-2',
          type: 'text' as const,
          title: 'Setting Up Your Development Environment',
          description: 'Learn how to set up Node.js, npm, and create your first React app',
          duration: 30,
          order: 2,
          textContent: `# Setting Up Your Development Environment

Before we start building with React, we need to set up our development environment. Here's what you'll need:

## Prerequisites
- Node.js (version 14 or higher)
- npm or yarn package manager
- A code editor (VS Code recommended)

## Installation Steps

### 1. Install Node.js
Download and install Node.js from [nodejs.org](https://nodejs.org). This will also install npm.

### 2. Create Your First React App
Use Create React App to set up a new project:

\`\`\`bash
npx create-react-app my-first-app
cd my-first-app
npm start
\`\`\`

### 3. Project Structure
Your new React app will have this structure:
- \`src/\` - Your application code
- \`public/\` - Static files
- \`package.json\` - Project dependencies
- \`README.md\` - Project documentation

## Next Steps
In the next lesson, we'll explore the project structure and write our first component!`,
          isRequired: true
        },
        {
          id: 'content-1-3',
          type: 'video' as const,
          title: 'Your First React Component',
          description: 'Create and understand your first React component',
          duration: 25,
          order: 3,
          videoUrl: 'https://example.com/first-component.mp4',
          isRequired: true
        }
      ],
      quiz: {
        id: 'quiz-1',
        courseId: 'react-fundamentals-2024',
        title: 'React Basics Quiz',
        description: 'Test your understanding of React fundamentals',
        passingScore: 70,
        timeLimit: 15,
        attempts: 3,
        order: 1,
        weight: 10,
        questions: [
          {
            id: 'q1-1',
            type: 'multiple-choice' as const,
            question: 'What is React primarily used for?',
            options: [
              'Building server-side applications',
              'Building user interfaces',
              'Database management',
              'Network programming'
            ],
            correctAnswer: 'Building user interfaces',
            explanation: 'React is a JavaScript library primarily used for building user interfaces, especially for web applications.',
            points: 10
          },
          {
            id: 'q1-2',
            type: 'multiple-choice' as const,
            question: 'Which command creates a new React application?',
            options: [
              'npm create react-app',
              'npx create-react-app',
              'npm install react',
              'react new app'
            ],
            correctAnswer: 'npx create-react-app',
            explanation: 'npx create-react-app is the recommended way to create a new React application with all necessary dependencies.',
            points: 10
          },
          {
            id: 'q1-3',
            type: 'true-false' as const,
            question: 'React components must always return JSX.',
            correctAnswer: 'true',
            explanation: 'React components must return JSX, which describes what the UI should look like.',
            points: 10
          }
        ]
      }
    },
    {
      id: 'module-2',
      title: 'JSX and Components',
      description: 'Learn JSX syntax and how to create and compose React components.',
      order: 2,
      estimatedHours: 5,
      estimatedMinutes: 300,
      content: [
        {
          id: 'content-2-1',
          type: 'video' as const,
          title: 'Understanding JSX',
          description: 'Learn JSX syntax and how it differs from HTML',
          duration: 20,
          order: 1,
          videoUrl: 'https://example.com/jsx-intro.mp4',
          isRequired: true
        },
        {
          id: 'content-2-2',
          type: 'video' as const,
          title: 'Creating React Components',
          description: 'Build your first functional components',
          duration: 30,
          order: 2,
          videoUrl: 'https://example.com/components.mp4',
          isRequired: true
        },
        {
          id: 'content-2-3',
          type: 'interactive' as const,
          title: 'Interactive JSX Practice',
          description: 'Practice writing JSX with interactive exercises',
          duration: 45,
          order: 3,
          interactiveData: {
            type: 'exercise',
            instructions: 'Create a Welcome component that displays a personalized greeting',
            initialCode: `function Welcome({ name }) {
  return <h1>Welcome, {name || 'Guest'}!</h1>;
}`,
            expectedOutput: 'Rendered output:\n<h1>Welcome, Guest!</h1>',
            hints: [
              'Use curly braces {} to embed JavaScript expressions in JSX',
              'The || operator provides a fallback value when name is undefined',
              'JSX elements must be properly closed and can contain dynamic content',
              'Try running the code to see how the JSX renders with the fallback value'
            ]
          },
          isRequired: true
        }
      ],
      assignment: {
        id: 'assignment-2',
        title: 'Build a Personal Profile Component',
        description: 'Create a React component that displays your personal profile information.',
        instructions: `Create a PersonalProfile component that includes:
1. Your name as a heading
2. A profile photo
3. A short bio paragraph
4. A list of your skills
5. Contact information

Requirements:
- Use proper JSX syntax
- Include at least 3 different HTML elements
- Use props to make the component reusable
- Style the component with CSS classes`,
        type: 'project' as const,
        submissionFormat: ['zip', 'github'],
        maxFileSize: 10,
        order: 1,
        weight: 15,
        rubric: [
          {
            criteria: 'Component Structure',
            description: 'Component is properly structured with correct JSX syntax',
            maxPoints: 25
          },
          {
            criteria: 'Props Usage',
            description: 'Props are used effectively to make the component reusable',
            maxPoints: 25
          },
          {
            criteria: 'Visual Design',
            description: 'Component is visually appealing and well-styled',
            maxPoints: 25
          },
          {
            criteria: 'Code Quality',
            description: 'Code is clean, readable, and follows best practices',
            maxPoints: 25
          }
        ]
      }
    },
    {
      id: 'module-3',
      title: 'Props and State',
      description: 'Master component communication with props and manage component state effectively.',
      order: 3,
      estimatedHours: 6,
      estimatedMinutes: 360,
      content: [
        {
          id: 'content-3-1',
          type: 'video' as const,
          title: 'Understanding Props',
          description: 'Learn how to pass data between components using props',
          duration: 25,
          order: 1,
          videoUrl: 'https://example.com/props.mp4',
          isRequired: true
        },
        {
          id: 'content-3-2',
          type: 'video' as const,
          title: 'Introduction to State',
          description: 'Learn about component state and the useState hook',
          duration: 30,
          order: 2,
          videoUrl: 'https://example.com/state.mp4',
          isRequired: true
        },
        {
          id: 'content-3-3',
          type: 'document' as const,
          title: 'Props vs State Cheat Sheet',
          description: 'Quick reference guide for when to use props vs state',
          duration: 10,
          order: 3,
          documentUrl: 'https://example.com/props-vs-state.pdf',
          isRequired: false
        }
      ]
    }
  ],
  
  quizzes: [
    {
      id: 'final-quiz',
      courseId: 'react-fundamentals-2024',
      title: 'React Fundamentals Final Assessment',
      description: 'Comprehensive assessment of your React knowledge',
      passingScore: 80,
      timeLimit: 60,
      attempts: 2,
      order: 1,
      weight: 30,
      questions: [
        {
          id: 'final-q1',
          type: 'multiple-choice' as const,
          question: 'Which React Hook is used for managing component state?',
          options: ['useEffect', 'useState', 'useContext', 'useReducer'],
          correctAnswer: 'useState',
          explanation: 'useState is the React Hook used for managing state in functional components.',
          points: 20
        },
        {
          id: 'final-q2',
          type: 'short-answer' as const,
          question: 'Explain the difference between props and state in React components.',
          correctAnswer: 'Props are read-only data passed from parent to child components, while state is mutable data managed within a component.',
          explanation: 'Props flow down from parent to child and should not be modified by the child. State is internal to a component and can be updated using setState or hooks.',
          points: 30
        }
      ]
    }
  ],

  marketData: {
    averageSalary: 85000,
    jobDemand: 92,
    salaryRange: { min: 60000, max: 120000 },
    relatedSkills: ['JavaScript', 'HTML', 'CSS', 'Node.js', 'Redux'],
    marketTrend: 'rising' as const
  },

  enrollmentCount: 1247,
  completionRate: 78,
  averageRating: 4.7,
  reviewCount: 189,
  createdBy: 'platform' as const,
  isPublic: true,
  createdAt: new Date('2024-01-15'),
  updatedAt: new Date('2024-01-20'),
  lastModified: new Date('2024-01-20'),
  version: '1.0'
};